import React, { useState } from 'react';
import Auth from 'j-toker';
import styled from 'styled-components';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import { getTranslatedString } from 'helpers/i18n';
import Details from './tabs/Details/Details';
import PurchaseOrders from './tabs/PurchaseOrders';
import Tasks from './tabs/Tasks';
import ResidentsContainer from './tabs/Residents/ResidentsContainer';
import History from './tabs/History/History';
import DrawerTabs from 'components/ui/DrawerTabs';
import MobileNavDropdown from 'components/ui/MobileNavDropdown/MobileNavDropdown';
import TicketCancelFormBody from './TicketCancelFormBody';
import successAnimationData from 'assets/success.json';
import loadingAnimationData from 'assets/loading.json';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';
import Lottie from 'react-lottie';
import { featureEnabled } from 'helpers/ToggleBot';
import CtaPrimary from 'components/ui/CtaPrimary';

const StyledTabPane = styled(DrawerTabs.TabPane)`
  height: 100% !important;
`;

const FORM_NAME = 'edit_ticket_form';

const DETAILS_TAB = '1';
const PO_TAB = '2';
const TASK_TAB = '3';
const RESIDENT_TAB = '4';
const HISTORY_TAB = '5';

const TicketViewing = ({ selectedTicket, handleClose, onSubmissionComplete, readOnly }) => {
  const [tabIndex, setTabIndex] = useState(DETAILS_TAB);
  const [cancelDialog, toggleCancelDialog] = useState(false);
  const [renderAnimationDrawer, setRenderAnimationDrawer] = useState(false);
  const [success, setSuccess] = useState(false);
  const [proposedScheduledResponse, setProposedScheduledResponse] = useState(false);

  const changeTab = (value) => {
    setTabIndex(value);
  };

  const handleCancelButtonClick = () => {
    toggleCancelDialog(!cancelDialog);
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const successOptions = {
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const isMobile = window.matchMedia('(max-width: 750px)').matches;

  let totalTasksCount = selectedTicket.tasks.length;
  let completedTasks = selectedTicket.tasks.filter((task) => task.complete).length;
  let showResidentsTab =
    selectedTicket.cotenants.length > 0 && Auth.user.type.toLowerCase() !== 'maintenance';

  const DetailsTab = (
    <Details
      formName={FORM_NAME}
      formData={selectedTicket}
      setIsLoading={(val) => setRenderAnimationDrawer(val)}
      setSuccess={(val) => setSuccess(val)}
      setProposedScheduledResponse={setProposedScheduledResponse}
      readOnly={readOnly}
      onSubmissionComplete={() =>
        onSubmissionComplete().then(() => setRenderAnimationDrawer(false))
      }
    />
  );

  const ResidentsContainerTab = (
    <ResidentsContainer
      ticketID={selectedTicket.id}
      setIsLoading={(val) => setRenderAnimationDrawer(val)}
      onSubmissionComplete={() =>
        onSubmissionComplete().then(() => setRenderAnimationDrawer(false))
      }
    />
  );

  if (success) {
    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={successOptions} height={150} width={150} />}
        headerText={getTranslatedString('globalDrawer.success')}
        bodyText={getTranslatedString('globalDrawer.ticketUpdated', {
          ticket_id: selectedTicket.ticket_number,
        })}
        cta={
          <CtaPrimary onClick={() => setSuccess(false)}>{getTranslatedString('close')}</CtaPrimary>
        }
      />
    );
  }

  if (proposedScheduledResponse === 'decline') {
    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={successOptions} height={150} width={150} />}
        headerText={getTranslatedString('globalDrawer.success')}
        bodyText={getTranslatedString('globalDrawer.proposedScheduledDateDenied')}
        cta={
          <CtaPrimary onClick={() => setProposedScheduledResponse(null)}>
            {getTranslatedString('close')}
          </CtaPrimary>
        }
      />
    );
  }

  return (
    <React.Fragment>
      <DrawerHeader
        noBorder
        onClose={handleClose}
        title={`# ${selectedTicket.ticket_number}`}
        subTitle={getTranslatedString('serviceRequestTitlecase')}
        status={selectedTicket.status}
      />
      <DrawerBody tabbedDrawerBodyStyle>
        {renderAnimationDrawer && (
          <DrawerSuccessMessages
            headerText={
              renderAnimationDrawer !== 'no_text'
                ? getTranslatedString('landlordPortal.maintenance.tickets.loadingHeader')
                : ''
            }
            bodyText={
              renderAnimationDrawer !== 'no_text'
                ? getTranslatedString('globalDrawer.loadingBody')
                : ''
            }
            headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
          />
        )}
        {!renderAnimationDrawer && cancelDialog && (
          <TicketCancelFormBody
            toggleCancelDialog={toggleCancelDialog}
            selectedTicket={selectedTicket}
            setIsLoading={() => setRenderAnimationDrawer(true)}
            onSubmissionComplete={() => {
              onSubmissionComplete().then(() => {
                setRenderAnimationDrawer(false);
                toggleCancelDialog(false);
              });
            }}
          />
        )}
        {!cancelDialog &&
          (isMobile ? (
            <React.Fragment>
              <MobileNavContainer
                tabIndex={tabIndex}
                onChange={changeTab}
                showResidentsTab={showResidentsTab}
              />
              <TabForms
                tabIndex={tabIndex}
                selectedTicket={selectedTicket}
                renderAnimationDrawer={renderAnimationDrawer}
                showResidentsTab={showResidentsTab}
                DetailsTab={DetailsTab}
                ResidentsContainerTab={ResidentsContainerTab}
              />
            </React.Fragment>
          ) : (
            <DrawerTabs defaultActiveKey={tabIndex} onChange={changeTab} color="white" padding="0">
              <StyledTabPane
                tab={getTranslatedString('landlordPortal.maintenance.tickets.details')}
                key={DETAILS_TAB}
              >
                {!renderAnimationDrawer && DetailsTab}
              </StyledTabPane>
              {featureEnabled('purchase_order') && (
                <StyledTabPane
                  tab={getTranslatedString('landlordPortal.maintenance.tickets.purchaseOrders')}
                  key={PO_TAB}
                >
                  <PurchaseOrders selectedTicket={selectedTicket} />
                </StyledTabPane>
              )}
              <StyledTabPane
                tab={getTranslatedString('landlordPortal.maintenance.tickets.tasks', {
                  completedTasks,
                  totalTasksCount,
                })}
                key={TASK_TAB}
              >
                <Tasks ticket={selectedTicket} ticketStatus={selectedTicket.status} />
              </StyledTabPane>
              {showResidentsTab && (
                <StyledTabPane
                  tab={getTranslatedString('landlordPortal.maintenance.tickets.residents')}
                  key={RESIDENT_TAB}
                >
                  {ResidentsContainerTab}
                </StyledTabPane>
              )}
              <StyledTabPane
                tab={getTranslatedString('landlordPortal.maintenance.tickets.history')}
                key={HISTORY_TAB}
              >
                <History events={selectedTicket.events} />
              </StyledTabPane>
            </DrawerTabs>
          ))}
      </DrawerBody>
      {renderDrawerFooter(
        tabIndex,
        handleCancelButtonClick,
        selectedTicket.status,
        cancelDialog,
        renderAnimationDrawer,
        readOnly
      )}
    </React.Fragment>
  );
};

const MobileNavContainer = ({ tabIndex, onChange, showResidentsTab }) => {
  const tabs = [
    {
      value: DETAILS_TAB,
      label: getTranslatedString('landlordPortal.maintenance.tickets.details'),
    },
    {
      value: PO_TAB,
      label: getTranslatedString('landlordPortal.maintenance.tickets.purchaseOrders'),
      hidden: !featureEnabled('purchase_order'),
    },
    {
      value: TASK_TAB,
      label: getTranslatedString('landlordPortal.landlordMaintenance.tasks'),
    },
    {
      value: RESIDENT_TAB,
      label: getTranslatedString('landlordPortal.maintenance.tickets.residents'),
      hidden: !showResidentsTab,
    },
    {
      value: HISTORY_TAB,
      label: getTranslatedString('landlordPortal.maintenance.tickets.history'),
    },
  ];

  const filteredTabs = tabs.filter((tab) => !tab.hidden);
  const currentTab = filteredTabs.find((tab) => tab.value === tabIndex);

  return <MobileNavDropdown tabs={filteredTabs} currentValue={currentTab} onChange={onChange} />;
};

const TabForms = ({
  tabIndex,
  selectedTicket,
  renderAnimationDrawer,
  showResidentsTab,
  DetailsTab,
  ResidentsContainerTab,
}) => {
  switch (tabIndex) {
    case DETAILS_TAB:
      return !renderAnimationDrawer && DetailsTab;
    case PO_TAB:
      return <PurchaseOrders selectedTicket={selectedTicket} />;
    case TASK_TAB:
      return <Tasks ticket={selectedTicket} ticketStatus={selectedTicket.status} />;
    case RESIDENT_TAB:
      return showResidentsTab && ResidentsContainerTab;
    case HISTORY_TAB:
      return <History events={selectedTicket.events} />;
    default:
      return !renderAnimationDrawer && DetailsTab;
  }
};

const renderDrawerFooter = (
  tabIndex,
  handleCancelButtonClick,
  currentStatus,
  cancelDialog,
  renderAnimationDrawer,
  readOnly
) => {
  if (tabIndex !== DETAILS_TAB) return;
  if (renderAnimationDrawer) return;

  let primaryActionText = getTranslatedString('update');
  let secondaryActionText = getTranslatedString('landlordPortal.maintenance.tickets.cancelTicket');
  let primaryActionFormName = FORM_NAME;

  if (cancelDialog) {
    primaryActionText = getTranslatedString(
      'landlordPortal.maintenance.tickets.cancelDialog.cancel'
    );
    secondaryActionText = getTranslatedString(
      'landlordPortal.maintenance.tickets.cancelDialog.close'
    );
    primaryActionFormName = 'cancel_ticket';
  }

  if (['cancelled', 'cancelled_dup', 'cancelled_no_res'].includes(currentStatus) || readOnly) {
    primaryActionText = getTranslatedString('update');
    secondaryActionText = null;
    primaryActionFormName = FORM_NAME;
  }

  return (
    <DrawerFooter
      primaryActionFormName={primaryActionFormName}
      primaryActionText={primaryActionText}
      secondaryActionText={secondaryActionText}
      onSecondaryActionClick={() => {
        handleCancelButtonClick();
      }}
    />
  );
};

export default TicketViewing;
